import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "qk-empty"
}
const _hoisted_2 = {
  key: 1,
  class: "qk-bar"
}
const _hoisted_3 = { class: "qk-kill" }
const _hoisted_4 = { class: "qk-amount" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "qk-save-inner" }
const _hoisted_7 = {
  key: 0,
  class: "qk-save-pulse"
}
const _hoisted_8 = {
  key: 1,
  class: "qk-check"
}
const _hoisted_9 = {
  key: 2,
  class: "qk-anchor"
}

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { NButton, NInputNumber, NSwitch, createDiscreteApi } from 'naive-ui';
import AnchorFloatPanel from '@/components/AnchorFloatPanel.vue';

const AMOUNT_KEY = 'kp_kill_amount_presets';
const MAX_AMOUNTS = 10;
/** 面板位置存放处 */
const POS_KEY = 'kp_quick_panel_pos';
/** 距离视口边缘的最小留白 */
const EDGE = 8;

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  originLeft: number;
  originTop: number;
}

const NO_DRAG_SELECTOR = 'input, textarea, select, button, a, .n-switch, .n-input, .n-button, .n-input-number, .qk-drop, .af-mini, .af-modal';


export default /*@__PURE__*/_defineComponent({
  __name: 'QuickKillPanel',
  props: {
    enabled: { type: Boolean },
    amount: {},
    roomCount: {},
    onSave: { type: Function },
    onRefresh: { type: Function },
    room: {},
    roomName: {}
  },
  setup(__props: any) {

const props = __props;

const api = createDiscreteApi(['message']);

/** 历史金额（保存过的门槛金额）存放处 */
const panelRef = ref<HTMLElement | null>(null);
/** null = 默认右下角（走 CSS），拖动后改为 left/top 绝对定位 */
const pos = ref<{ left: number; top: number } | null>(null);
const dragging = ref(false);

const saving = ref(false);
const draftEnabled = ref(props.enabled);
const draftAmount = ref<number | null>(props.amount ?? 0);

/** 用户手动改过开关/金额后，不再被外部同步覆盖 */
let enabledDirty = false;
let amountDirty = false;

/** 保存过的金额（下拉候选，升序展示） */
const amountOptions = ref<number[]>([]);
/** 输入框是否聚焦：聚焦且金额有效时，上方浮出历史金额 */
const amountFocused = ref(false);
let blurTimer: number | null = null;

const normalizedAmount = computed(() => {
  const v = draftAmount.value;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return Math.round(v * 100) / 100;
});

/** 输入框里是正常金额（非空、非负）才展示下拉 */
const hasValidAmount = computed(
  () => typeof draftAmount.value === 'number' && Number.isFinite(draftAmount.value) && draftAmount.value >= 0
);

const showAmountDrop = computed(
  () => amountFocused.value && hasValidAmount.value && amountOptions.value.length > 0
);

/** 服务端当前的门槛金额（保存基线，与面板草稿同精度） */
const baselineAmount = computed(() => {
  const v = props.amount;
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return Math.round(v * 100) / 100;
});

/** 是否有未保存的变更：开关或门槛金额任一与基线不同 */
const hasChanges = computed(
  () => draftEnabled.value !== props.enabled || normalizedAmount.value !== baselineAmount.value
);

/** 是否有可用房间：没有房间就没有可控制对象，面板只显示锁定提示 */
const hasRooms = computed(() => props.roomCount > 0);

/** 面板贴近屏幕顶部时，下拉改为向下展开（否则会被视口裁掉） */
const dropBelow = computed(() => (pos.value ? pos.value.top < 170 : false));

const panelStyle = computed(() =>
  pos.value
    ? {
        left: `${pos.value.left}px`,
        top: `${pos.value.top}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : {}
);

// 服务端状态回来后同步到面板（用户正在编辑的字段不覆盖）
watch(
  () => props.enabled,
  (v) => {
    if (!enabledDirty) draftEnabled.value = v;
  }
);
watch(
  () => props.amount,
  (v) => {
    if (!amountDirty) draftAmount.value = v ?? 0;
  }
);

// ---------- 拖拽 ----------
let dragState: DragState | null = null;
/** 本次按下是否真的移动过（避免“点一下”也把定位方式切走） */
let dragMoved = false;

function clampPosition(left: number, top: number) {
  const el = panelRef.value;
  const width = el?.offsetWidth ?? 320;
  const height = el?.offsetHeight ?? 44;
  const maxLeft = Math.max(EDGE, window.innerWidth - width - EDGE);
  const maxTop = Math.max(EDGE, window.innerHeight - height - EDGE);
  return {
    left: Math.min(Math.max(EDGE, left), maxLeft),
    top: Math.min(Math.max(EDGE, top), maxTop),
  };
}

/** 面板内的可交互控件不参与拖拽（.af-mini 是嵌入的锚点小地图；.af-modal 是展开的大面板，点它应关闭而非拖拽） */
function onDragStart(e: PointerEvent) {
  if (e.button !== 0) return;
  const el = panelRef.value;
  if (!el) return;

  // 拖到开关/输入框/按钮上时交给控件自己处理
  const target = e.target as HTMLElement | null;
  if (target && target.closest(NO_DRAG_SELECTOR)) return;

  const rect = el.getBoundingClientRect();
  dragState = {
    pointerId: e.pointerId,
    startX: e.clientX,
    startY: e.clientY,
    originLeft: rect.left,
    originTop: rect.top,
  };
  dragMoved = false;
  dragging.value = true;

  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
}

function onDragMove(e: PointerEvent) {
  if (!dragState || e.pointerId !== dragState.pointerId) return;
  const dx = e.clientX - dragState.startX;
  const dy = e.clientY - dragState.startY;
  if (!dragMoved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
  dragMoved = true;
  pos.value = clampPosition(dragState.originLeft + dx, dragState.originTop + dy);
}

function onDragEnd(e: PointerEvent) {
  if (!dragState || e.pointerId !== dragState.pointerId) return;
  const target = e.currentTarget as HTMLElement;
  if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);

  dragState = null;
  dragging.value = false;

  if (dragMoved && pos.value) {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos.value));
    } catch (err) {
      // 存不下就只在本次会话生效
    }
  }
}

function readSavedPos(): { left: number; top: number } | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return { left: parsed.left, top: parsed.top };
    }
  } catch (e) {
    // 忽略损坏的配置
  }
  return null;
}

function onWindowResize() {
  if (pos.value) pos.value = clampPosition(pos.value.left, pos.value.top);
}

/** 双击把手回到默认的右下角 */
function resetPos() {
  pos.value = null;
  try {
    localStorage.removeItem(POS_KEY);
  } catch (e) {
    // 忽略
  }
}

// ---------- 历史金额 ----------
function loadAmountOptions() {
  try {
    const raw = localStorage.getItem(AMOUNT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    amountOptions.value = Array.isArray(arr)
      ? arr
          .filter((x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= 0)
          .map((x: number) => Math.round(x * 100) / 100)
          .slice(0, MAX_AMOUNTS)
          .sort((a, b) => a - b)
      : [];
  } catch (e) {
    amountOptions.value = [];
  }
}

function persistAmountOptions() {
  try {
    localStorage.setItem(AMOUNT_KEY, JSON.stringify(amountOptions.value));
  } catch (e) {
    // localStorage 不可用时忽略，候选只在本次会话生效
  }
}

/** 保存成功后把金额记入下拉候选：去重、只保留最近 10 个、升序展示 */
function rememberAmount(value: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return;
  amountOptions.value = [value, ...amountOptions.value.filter((x) => x !== value)]
    .slice(0, MAX_AMOUNTS)
    .sort((a, b) => a - b);
  persistAmountOptions();
}

function onEnabledChange(v: boolean) {
  draftEnabled.value = v;
  enabledDirty = true;
}

function onAmountChange(v: number | null) {
  // 允许输入过程中清空，但负数一律按 0 处理（最低 0）
  draftAmount.value = v === null ? null : Math.max(0, v);
  amountDirty = true;
}

/** 失焦/保存时收敛草稿：空值、非法值、负数一律落成 0，不存空 */
function normalizeAmountDraft() {
  const v = draftAmount.value;
  const next = typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  if (draftAmount.value !== next) {
    draftAmount.value = next;
    amountDirty = true;
  }
}

function pickAmount(value: number) {
  draftAmount.value = value;
  amountDirty = true;
}

function clearBlurTimer() {
  if (blurTimer) {
    clearTimeout(blurTimer);
    blurTimer = null;
  }
}

function onAmountFocusIn() {
  clearBlurTimer();
  amountFocused.value = true;
}

function onAmountFocusOut() {
  // 离开输入框就收敛：空 / 负数一律变 0，输入框里不留空
  normalizeAmountDraft();

  // 延迟收起：点击下拉项时 mousedown 已被阻止，这里只兜底输入框真失焦
  clearBlurTimer();
  blurTimer = window.setTimeout(() => {
    amountFocused.value = false;
    blurTimer = null;
  }, 120);
}

async function handleSave() {
  if (saving.value) return;
  // 注意：无变更时也允许保存（按钮此时显示“已保存”，点击等于强制再提交一次）
  saving.value = true;
  try {
    // 兜底：即便没走失焦流程，空值也按 0 提交
    normalizeAmountDraft();
    const amount = normalizedAmount.value;
    const ok = await props.onSave({ enabled: draftEnabled.value, amount });
    if (ok) {
      // 保存成功 → 该金额进入输入框上方的历史下拉
      rememberAmount(amount);
      draftAmount.value = amount; // 空值保存后显示 0，不留空
      enabledDirty = false;
      amountDirty = false;
      api.message.success('全局配置已保存');
    } else {
      api.message.error('保存失败');
    }
  } catch (e) {
    api.message.error(e instanceof Error && e.message ? e.message : '保存失败');
  } finally {
    saving.value = false;
  }
}

loadAmountOptions();
onMounted(() => {
  const saved = readSavedPos();
  if (saved) pos.value = clampPosition(saved.left, saved.top);
  window.addEventListener('resize', onWindowResize);

  // 连接可能还没就绪导致房间数为 0，补拉一次
  if (props.roomCount === 0) props.onRefresh?.();
});
onUnmounted(() => {
  clearBlurTimer();
  window.removeEventListener('resize', onWindowResize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "panelRef",
    ref: panelRef,
    class: _normalizeClass(["qk-panel", { dragging: dragging.value }]),
    style: _normalizeStyle(panelStyle.value),
    onPointerdown: onDragStart,
    onPointermove: onDragMove,
    onPointerup: onDragEnd,
    onPointercancel: onDragEnd
  }, [
    _createElementVNode("div", {
      class: "qk-dragbar",
      title: "按住拖动，双击复位",
      onDblclick: resetPos
    }, null, 32),
    (!hasRooms.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, "无可用房间，相关功能已锁定"))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[2] || (_cache[2] = _createElementVNode("span", {
              class: "qk-label",
              title: "全部房间的杀猪总开关"
            }, "总控", -1)),
            _createVNode(_unref(NSwitch), {
              value: draftEnabled.value,
              size: "small",
              disabled: saving.value,
              title: draftEnabled.value ? '关闭全部房间' : '开启全部房间',
              "onUpdate:value": onEnabledChange
            }, null, 8, ["value", "disabled", "title"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "qk-label" }, "门槛", -1)),
            _createElementVNode("div", {
              class: "qk-input-wrap",
              onFocusin: onAmountFocusIn,
              onFocusout: onAmountFocusOut
            }, [
              _createVNode(_unref(NInputNumber), {
                value: draftAmount.value,
                class: "qk-input",
                min: 0,
                "show-button": false,
                size: "small",
                placeholder: "0",
                "onUpdate:value": onAmountChange
              }, null, 8, ["value"]),
              (showAmountDrop.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["qk-drop", { below: dropBelow.value }]),
                    onPointerdown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
                    onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(amountOptions.value, (p) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: p,
                        class: _normalizeClass(["qk-drop-item", { active: normalizedAmount.value === p }]),
                        onClick: ($event: any) => (pickAmount(p))
                      }, _toDisplayString(p), 11, _hoisted_5))
                    }), 128))
                  ], 34))
                : _createCommentVNode("", true)
            ], 32)
          ]),
          _createVNode(_unref(NButton), {
            size: "small",
            type: hasChanges.value ? 'primary' : 'success',
            loading: saving.value,
            title: hasChanges.value ? '有未保存的变更，点击保存' : '当前已保存，点击可再次提交',
            onClick: handleSave
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_6, [
                (hasChanges.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7))
                  : (_openBlock(), _createElementBlock("span", _hoisted_8, "✓")),
                _createTextVNode(" " + _toDisplayString(hasChanges.value ? '有变更' : '已保存'), 1)
              ])
            ]),
            _: 1
          }, 8, ["type", "loading", "title"])
        ])),
    (hasRooms.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(AnchorFloatPanel, {
            embedded: "",
            room: _ctx.room,
            "room-name": _ctx.roomName
          }, null, 8, ["room", "room-name"])
        ]))
      : _createCommentVNode("", true)
  ], 38))
}
}

})